import { useState, useRef, useCallback, useEffect } from "react";

export type MicPermission = "granted" | "prompt" | "denied" | "unavailable";

export interface UseAudioRecorderOptions {
  onRecordingComplete?: (blob: Blob) => void;
  maxDurationSeconds?: number;
}

export interface UseAudioRecorderReturn {
  isRecording: boolean;
  recordingTime: number;
  micPermission: MicPermission;
  deviceError: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  recheckPermission: () => Promise<void>;
  clearDeviceError: () => void;
  formatTime: (seconds: number) => string;
  isNearLimit: boolean;
  maxDurationSeconds: number;
}

// MIME type priority kept in sync with lib/integrations-openai-ai-react/src/audio/recorder-utils.ts
// (canonical source). If updating here, update there too.
const MIME_TYPE_PRIORITY = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

export const DEFAULT_MAX_DURATION = 300;
export const MAX_BLOB_BYTES = 18 * 1024 * 1024;
const MIN_RECORDING_SECONDS = 8;

export function getSupportedMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  for (const type of MIME_TYPE_PRIORITY) {
    try {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    } catch {
    }
  }
  return "";
}

async function queryMicPermissionState(): Promise<MicPermission> {
  try {
    if (!navigator.permissions) return "prompt";
    const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
    return result.state as MicPermission;
  } catch {
    return "prompt";
  }
}

function stopStream(stream: MediaStream | null) {
  if (stream) {
    stream.getTracks().forEach((t) => {
      t.onended = null;
      t.stop();
    });
  }
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iP(hone|od|ad)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function getPreferredMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  if (isIOS() || isSafari()) {
    const iosCandidates = ["audio/mp4;codecs=mp4a.40.2", "audio/mp4"];
    for (const type of iosCandidates) {
      try {
        if (MediaRecorder.isTypeSupported(type)) return type;
      } catch { }
    }
  }
  return getSupportedMimeType();
}

export function useAudioRecorder(options: UseAudioRecorderOptions = {}): UseAudioRecorderReturn {
  const maxDurationSeconds = options.maxDurationSeconds ?? DEFAULT_MAX_DURATION;

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [micPermission, setMicPermission] = useState<MicPermission>("prompt");
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onRecordingCompleteRef = useRef(options.onRecordingComplete);
  const isStartingRef = useRef(false);
  const ownStopRef = useRef(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    onRecordingCompleteRef.current = options.onRecordingComplete;
  });

  useEffect(() => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setMicPermission("unavailable");
      return;
    }
    queryMicPermissionState().then(setMicPermission);
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      const wl = wakeLockRef.current;
      wakeLockRef.current = null;
      wl.release().catch(() => {});
    }
  }, []);

  const stopTimerAndStream = useCallback(() => {
    releaseWakeLock();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const s = streamRef.current;
    streamRef.current = null;
    stopStream(s);
  }, [releaseWakeLock]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopStream(streamRef.current);
      streamRef.current = null;
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && mediaRecorderRef.current?.state === "recording") {
        ownStopRef.current = true;
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const clearDeviceError = useCallback(() => {
    setDeviceError(null);
  }, []);

  const handleRecordingError = useCallback(async (err: unknown) => {
    const errorName = err instanceof DOMException ? err.name : "";

    if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError" || errorName === "SecurityError") {
      const permState = await queryMicPermissionState();
      if (permState === "denied") {
        setMicPermission("denied");
      } else {
        setDeviceError("Allow microphone access when your browser asks, then tap the mic button again.");
      }
      return;
    }

    if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
      setDeviceError("No microphone detected. Please connect a microphone and try again.");
    } else if (errorName === "NotReadableError" || errorName === "TrackStartError") {
      setDeviceError("Your microphone is in use by another app. Close other apps using it and try again.");
    } else if (errorName === "AbortError") {
      setDeviceError("Microphone access was interrupted. Please try again.");
    } else if (errorName === "InvalidStateError") {
      setDeviceError("Audio recorder is in an invalid state. Please refresh the page and try again.");
    } else if (errorName === "OverconstrainedError") {
      setDeviceError("Microphone does not meet required constraints. Please try a different microphone.");
    } else {
      setDeviceError("Could not access your microphone. Please check your device and try again.");
    }
  }, []);

  const acquireWakeLock = useCallback(() => {
    if (typeof navigator === "undefined") return;
    if (!("wakeLock" in navigator)) return;
    (navigator as Navigator & { wakeLock: { request: (type: string) => Promise<WakeLockSentinel> } }).wakeLock
      .request("screen")
      .then((wl) => {
        wakeLockRef.current = wl;
        wl.addEventListener("release", () => {
          if (wakeLockRef.current === wl) wakeLockRef.current = null;
        });
      })
      .catch(() => {});
  }, []);

  const actuallyStartRecording = useCallback(async () => {
    setDeviceError(null);
    ownStopRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      isStartingRef.current = false;
      throw err;
    }

    streamRef.current = stream;
    setMicPermission("granted");

    const mimeType = getPreferredMimeType();
    let mediaRecorder: MediaRecorder;
    try {
      mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch {
      isStartingRef.current = false;
      stopStream(stream);
      streamRef.current = null;
      try {
        const fallbackRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = fallbackRecorder;
      } catch {
        setDeviceError("Your browser could not start the recorder. Please try a different browser.");
        return;
      }
      setDeviceError("Your browser could not start the recorder. Please try a different browser.");
      return;
    }

    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onerror = () => {
      if (mediaRecorderRef.current?.state !== "inactive") {
        ownStopRef.current = true;
        try { mediaRecorderRef.current?.stop(); } catch { }
      }
      setIsRecording(false);
      stopTimerAndStream();
      setDeviceError("Recording was interrupted by a device error. Please try again.");
    };

    mediaRecorder.onstop = () => {
      stopTimerAndStream();
      isStartingRef.current = false;

      const blobType = mimeType || mediaRecorder.mimeType || "";
      const audioBlob = blobType
        ? new Blob(chunksRef.current, { type: blobType })
        : new Blob(chunksRef.current);

      if (audioBlob.size === 0) {
        setDeviceError("No audio was captured. Please try recording again.");
        return;
      }

      if (audioBlob.size > MAX_BLOB_BYTES) {
        setDeviceError(
          `Recording is too large (${Math.round(audioBlob.size / 1024 / 1024)}MB). ` +
          "Try a shorter recording — aim for 1–3 minutes."
        );
        return;
      }

      onRecordingCompleteRef.current?.(audioBlob);
    };

    stream.getTracks().forEach((track) => {
      track.onended = () => {
        if (ownStopRef.current) return;
        setIsRecording(false);
        stopTimerAndStream();
        mediaRecorderRef.current = null;
        setDeviceError("Microphone disconnected during recording. Please try again.");
      };
    });

    mediaRecorder.start();
    isStartingRef.current = false;
    setIsRecording(true);
    setRecordingTime(0);

    acquireWakeLock();

    timerRef.current = window.setInterval(() => {
      setRecordingTime((prev) => {
        const next = prev + 1;
        if (next >= maxDurationSeconds) {
          if (mediaRecorderRef.current?.state === "recording") {
            ownStopRef.current = true;
            mediaRecorderRef.current.stop();
            setIsRecording(false);
          }
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
        return next;
      });
    }, 1000);
  }, [maxDurationSeconds, stopTimerAndStream, acquireWakeLock]);

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setMicPermission("unavailable");
      return;
    }
    if (micPermission === "denied") return;
    if (isStartingRef.current || mediaRecorderRef.current?.state === "recording") return;

    isStartingRef.current = true;
    try {
      await actuallyStartRecording();
    } catch (err) {
      isStartingRef.current = false;
      await handleRecordingError(err);
    }
  }, [micPermission, actuallyStartRecording, handleRecordingError]);

  const recheckPermission = useCallback(async () => {
    setDeviceError(null);
    setMicPermission("prompt");
    if (isStartingRef.current || mediaRecorderRef.current?.state === "recording") return;

    isStartingRef.current = true;
    try {
      await actuallyStartRecording();
    } catch (err) {
      isStartingRef.current = false;
      await handleRecordingError(err);
    }
  }, [actuallyStartRecording, handleRecordingError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      ownStopRef.current = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const isNearLimit = isRecording && recordingTime >= maxDurationSeconds - 60;

  return {
    isRecording,
    recordingTime,
    micPermission,
    deviceError,
    startRecording,
    stopRecording,
    recheckPermission,
    clearDeviceError,
    formatTime,
    isNearLimit,
    maxDurationSeconds,
  };
}

export const MIN_RECORDING_SECS = MIN_RECORDING_SECONDS;
